import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import BijlagenPanel from '@/components/BijlagenPanel';
import {
  PencilSimple, Check, X, Plus, Trash, MapPin, Buildings, Notebook, Phone,
  UsersThree, SlidersHorizontal, Bank as BankIcon, Receipt, MagnifyingGlass,
  CaretRight, House, Info, FloppyDisk, ArrowBendUpLeft, CaretDown,
  CheckSquare, PaperPlaneTilt, FileZip, FileCsv, Stack,
} from '@phosphor-icons/react';
import IOSToggle from '@/components/IOSToggle';
import { usePageNavigation } from '@/contexts/PageNavigationContext';

const TALEN = [
  { code: '', label: 'Standaard' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'fr', label: 'Frans' },
  { code: 'en', label: 'Engels' },
  { code: 'de', label: 'Duits' },
];

// ── Card wrapper met Aanpassen-toggle ─────────────────────────────
function Card({ icon: Icon, title, badge, actions, children, testid }) {
  return (
    <div className="bg-white border border-gray-200 flex flex-col" data-testid={testid}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-500">
          {Icon && <Icon size={14} weight="bold" />}
          <span>{title}</span>
          {badge !== undefined && badge !== null && (
            <span className="ml-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-bold">{badge}</span>
          )}
        </div>
        <div className="flex items-center gap-1">{actions}</div>
      </div>
      <div className="p-4 text-sm flex-1">{children}</div>
    </div>
  );
}

function IconBtn({ icon: Icon, label, onClick, testid, danger }) {
  return (
    <button className={`flex items-center gap-1 px-2 py-1 text-[11px] font-medium border border-transparent hover:border-gray-200 ${danger ? 'text-red-500 hover:text-red-700' : 'text-gray-500 hover:text-[#002FA7]'}`}
      onClick={onClick} data-testid={testid}>
      <Icon size={12} /> {label}
    </button>
  );
}

export default function LeverancierDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { goBack } = usePageNavigation();
  const [lev, setLev] = useState(null);
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [editMode, setEditMode] = useState(null); // 'bedrijf'|'contact'|'notities'|'adres'|'contactpersonen'|'voorkeuren'|'bankrekeningen'|null
  const [draft, setDraft] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkMenuRef = useRef(null);

  useEffect(() => {
    if (!bulkMenuOpen) return;
    const h = (e) => { if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target)) setBulkMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [bulkMenuOpen]);

  const load = async () => {
    try {
      const [lr, dr] = await Promise.all([
        API.get(`/leveranciers/${id}`),
        API.get(`/leveranciers/${id}/documenten`),
      ]);
      setLev(lr.data);
      setDocs(dr.data || []);
    } catch (e) {
      if (e.response?.status === 404) navigate('/leveranciers');
    }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const startEdit = (section) => {
    if (!lev) return;
    setDraft({
      bedrijf: lev.bedrijf || '', btw: lev.btw || '', peppol_enabled: !!lev.peppol_enabled,
      email: lev.email || '', telefoon: lev.telefoon || '', website: lev.website || '',
      notities: lev.notities || '',
      straat: lev.straat || '', huisnr: lev.huisnr || '', postcode: lev.postcode || '',
      gemeente: lev.gemeente || '', land: lev.land || 'BE',
      actief: lev.actief !== false, taal: lev.taal || '',
      contactpersonen: Array.isArray(lev.contactpersonen) ? [...lev.contactpersonen] : [],
      bankrekeningen: Array.isArray(lev.bankrekeningen) ? [...lev.bankrekeningen] : [],
    });
    setEditMode(section);
  };

  const saveEdit = async (fields) => {
    const payload = {};
    fields.forEach(f => { payload[f] = draft[f]; });
    try {
      await API.put(`/leveranciers/${id}`, payload);
      setEditMode(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Opslaan mislukt');
    }
  };

  const cancelEdit = () => setEditMode(null);

  const delLev = async () => {
    if ((lev.aantal_docs || 0) > 0) {
      alert(`Deze leverancier heeft ${lev.aantal_docs} gekoppelde document(en) en kan niet verwijderd worden.`);
      return;
    }
    if (!window.confirm(`Leverancier "${lev.bedrijf}" wissen?`)) return;
    try {
      await API.delete(`/leveranciers/${id}`);
      navigate('/leveranciers');
    } catch (e) {
      alert(e.response?.data?.detail || 'Wissen mislukt');
    }
  };

  const mapsUrl = useMemo(() => {
    if (!lev) return '';
    const parts = [lev.straat, lev.huisnr, lev.postcode, lev.gemeente, lev.land].filter(Boolean).join(' ');
    return `https://www.google.com/maps?q=${encodeURIComponent(parts)}`;
  }, [lev]);

  // Aankopen filter + pagination
  const filteredDocs = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return docs;
    return docs.filter(d =>
      (d.doc_nummer || '').toLowerCase().includes(s) ||
      (d.doc_datum || '').toLowerCase().includes(s) ||
      String(d.totaal || '').includes(s)
    );
  }, [docs, q]);
  const totalPages = Math.max(1, Math.ceil(filteredDocs.length / pageSize));
  const curPage = Math.min(page, totalPages);
  const pagedDocs = filteredDocs.slice((curPage - 1) * pageSize, curPage * pageSize);
  useEffect(() => { setPage(1); }, [q, pageSize]);

  const fmtEur = (n) => (Number(n) || 0).toLocaleString('nl-BE', { style: 'currency', currency: 'EUR' });
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
  const daysLeft = (vervaldatum) => {
    if (!vervaldatum) return null;
    const d = new Date(vervaldatum);
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const diff = Math.round((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // ── Selectie + bulk-acties ───────────────────────────────────────
  const toggleSelect = (id) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleSelectAllPage = () => {
    const pageIds = pagedDocs.map(d => d.id);
    const allSelected = pageIds.every(i => selected.has(i));
    setSelected(s => {
      const n = new Set(s);
      if (allSelected) pageIds.forEach(i => n.delete(i));
      else pageIds.forEach(i => n.add(i));
      return n;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const bulkAction = async (actie, label) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(`${label} voor ${ids.length} document(en)?`)) return;
    setBulkBusy(true); setBulkMenuOpen(false);
    try {
      const r = await API.post(`/leveranciers/${id}/bulk-actie`, { actie, doc_ids: ids });
      alert(`${r.data.aantal} document(en) bijgewerkt.`);
      clearSelection();
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Actie mislukt');
    }
    setBulkBusy(false);
  };

  const bulkDownload = async (kind) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBulkBusy(true); setBulkMenuOpen(false);
    try {
      const endpoint = kind === 'zip' ? 'bulk-export-zip' : 'bulk-export-csv';
      const r = await API.post(`/leveranciers/${id}/${endpoint}`, { doc_ids: ids }, { responseType: 'blob' });
      const ext = kind === 'zip' ? 'zip' : 'csv';
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `leverancier-${lev.bedrijf.replace(/[^a-zA-Z0-9]/g, '_')}-${ids.length}docs.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Download mislukt');
    }
    setBulkBusy(false);
  };

  if (!lev) return <div className="text-gray-400 text-sm p-6">Laden...</div>;

  const addContactpersoon = () => setDraft(p => ({ ...p, contactpersonen: [...(p.contactpersonen || []), { naam: '', functie: '', email: '', telefoon: '' }] }));
  const updContactpersoon = (i, k, v) => setDraft(p => ({ ...p, contactpersonen: p.contactpersonen.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const delContactpersoon = (i) => setDraft(p => ({ ...p, contactpersonen: p.contactpersonen.filter((_, j) => j !== i) }));

  const addBankrek = () => setDraft(p => ({ ...p, bankrekeningen: [...(p.bankrekeningen || []), { iban: '', bic: '', rekeninghouder: '' }] }));
  const updBankrek = (i, k, v) => setDraft(p => ({ ...p, bankrekeningen: p.bankrekeningen.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const delBankrek = (i) => setDraft(p => ({ ...p, bankrekeningen: p.bankrekeningen.filter((_, j) => j !== i) }));

  const contactpersonenDisp = Array.isArray(lev.contactpersonen) ? lev.contactpersonen : [];
  const bankrekeningenDisp = Array.isArray(lev.bankrekeningen) ? lev.bankrekeningen : [];

  return (
    <div data-testid="leverancier-detail-page" className="pb-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-4">
        <Link to="/dashboard" className="flex items-center gap-1 hover:text-[#002FA7]"><House size={12} /> Dashboard</Link>
        <CaretRight size={10} className="text-gray-300" />
        <Link to="/leveranciers" className="hover:text-[#002FA7]">Leveranciers</Link>
        <CaretRight size={10} className="text-gray-300" />
        <span className="text-gray-900 font-medium">{lev.bedrijf}</span>
      </div>

      {/* Title + global actions */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{lev.bedrijf}</h1>
          <div className="text-xs text-gray-400 mt-0.5">
            {lev.aantal_docs} aankoop/aankopen — totaal <span className="font-bold text-gray-600">{fmtEur(lev.totaal_bedrag)}</span>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          {/* Meerdere documenten omzetten */}
          <div className="relative" ref={bulkMenuRef}>
            <button className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border transition-colors ${selected.size > 0 ? 'border-[#002FA7] bg-[#002FA7] text-white' : 'border-gray-200 bg-white text-gray-500 cursor-not-allowed'}`}
              disabled={selected.size === 0 || bulkBusy}
              onClick={() => setBulkMenuOpen(o => !o)}
              data-testid="bulk-menu-btn">
              <Stack size={14} weight="bold" />
              {selected.size > 0 ? `${selected.size} document(en) omzetten` : 'Meerdere documenten omzetten'}
              <CaretDown size={12} />
            </button>
            {bulkMenuOpen && selected.size > 0 && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 shadow-xl min-w-[260px] z-20" data-testid="bulk-menu">
                <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#F0F4FF] flex items-center gap-2.5 border-b border-gray-100" onClick={() => bulkAction('betaald', 'Markeer als betaald')} data-testid="bulk-betaald">
                  <CheckSquare size={14} className="text-[#059669]" /> Markeer als betaald
                </button>
                <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#F0F4FF] flex items-center gap-2.5 border-b border-gray-100" onClick={() => bulkAction('accountant-verzonden', 'Markeer als verzonden naar accountant')} data-testid="bulk-accountant">
                  <PaperPlaneTilt size={14} className="text-[#002FA7]" /> Naar accountant verzonden
                </button>
                <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#F0F4FF] flex items-center gap-2.5 border-b border-gray-100" onClick={() => bulkAction('accountant-ongedaan', 'Accountant-status wissen')}>
                  <X size={14} className="text-gray-400" /> Accountant-status wissen
                </button>
                <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#F0F4FF] flex items-center gap-2.5 border-b border-gray-100" onClick={() => bulkDownload('zip')} data-testid="bulk-zip">
                  <FileZip size={14} className="text-amber-600" /> Download als ZIP
                </button>
                <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#F0F4FF] flex items-center gap-2.5" onClick={() => bulkDownload('csv')} data-testid="bulk-csv">
                  <FileCsv size={14} className="text-green-600" /> Export als CSV
                </button>
              </div>
            )}
          </div>
          <button type="button" onClick={() => goBack('/leveranciers')} className="btn btn-ghost btn-sm" data-testid="supplier-back-button"><ArrowBendUpLeft size={14} /> Terug</button>
        </div>
      </div>

      {/* 3-column card grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">

        {/* ── Bedrijf ── */}
        <Card icon={Buildings} title="Bedrijf" badge={lev.aantal_docs}
          actions={editMode === 'bedrijf' ? (
            <>
              <IconBtn icon={FloppyDisk} label="Opslaan" onClick={() => saveEdit(['bedrijf', 'btw', 'peppol_enabled'])} testid="save-bedrijf" />
              <IconBtn icon={X} label="Annuleren" onClick={cancelEdit} />
            </>
          ) : (
            <>
              <IconBtn icon={PencilSimple} label="Aanpassen" onClick={() => startEdit('bedrijf')} testid="edit-bedrijf" />
              <IconBtn icon={Trash} label="Wissen" onClick={delLev} testid="del-lev" danger />
            </>
          )}
          testid="card-bedrijf">
          {editMode === 'bedrijf' ? (
            <div className="space-y-2">
              <div><label className="form-label">Bedrijfsnaam</label><input className="field-input" value={draft.bedrijf} onChange={e => setDraft(p => ({ ...p, bedrijf: e.target.value }))} /></div>
              <div><label className="form-label">BTW-nummer</label><input className="field-input font-mono text-xs" value={draft.btw} onChange={e => setDraft(p => ({ ...p, btw: e.target.value }))} /></div>
              <div className="flex items-center gap-2">
                <IOSToggle checked={draft.peppol_enabled} onChange={v => setDraft(p => ({ ...p, peppol_enabled: v }))} size="sm" label="Aangesloten op Peppol" testid="toggle-peppol" />
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="text-base font-semibold text-gray-900">{lev.bedrijf}</div>
              {lev.btw && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-gray-600">{lev.btw}</span>
                  <a
                    href={`https://www.checkinhoudingsplicht.be/?ondernemingsnummer=${encodeURIComponent((lev.btw || '').replace(/^BE/i, '').replace(/[.\s]/g, ''))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors"
                    title="Open de officiële controle van RSZ/Fiscale inhoudingsplicht in een nieuw tabblad"
                    data-testid="check-inhouding-leverancier"
                  >
                    🛡️ Check Inhoudingsplicht
                  </a>
                </div>
              )}
              {lev.peppol_enabled && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#002FA7] text-white text-[10px] font-bold mt-1">
                  Aangesloten op Peppol
                </span>
              )}
            </div>
          )}
        </Card>

        {/* ── Contactgegevens ── */}
        <Card icon={Phone} title="Contactgegevens"
          actions={editMode === 'contact' ? (
            <>
              <IconBtn icon={FloppyDisk} label="Opslaan" onClick={() => saveEdit(['email', 'telefoon', 'website'])} testid="save-contact" />
              <IconBtn icon={X} label="Annuleren" onClick={cancelEdit} />
            </>
          ) : (
            <IconBtn icon={PencilSimple} label="Aanpassen" onClick={() => startEdit('contact')} testid="edit-contact" />
          )}
          testid="card-contact">
          {editMode === 'contact' ? (
            <div className="space-y-2">
              <div><label className="form-label">E-mail</label><input className="field-input" value={draft.email} onChange={e => setDraft(p => ({ ...p, email: e.target.value }))} /></div>
              <div><label className="form-label">Telefoon</label><input className="field-input" value={draft.telefoon} onChange={e => setDraft(p => ({ ...p, telefoon: e.target.value }))} /></div>
              <div><label className="form-label">Website</label><input className="field-input" value={draft.website} onChange={e => setDraft(p => ({ ...p, website: e.target.value }))} /></div>
            </div>
          ) : (lev.email || lev.telefoon || lev.website) ? (
            <div className="space-y-1 text-xs">
              {lev.email && <div><span className="text-gray-400 mr-2">E-mail:</span><a href={`mailto:${lev.email}`} className="text-[#002FA7] hover:underline">{lev.email}</a></div>}
              {lev.telefoon && <div><span className="text-gray-400 mr-2">Telefoon:</span><a href={`tel:${lev.telefoon}`} className="text-[#002FA7] hover:underline">{lev.telefoon}</a></div>}
              {lev.website && <div><span className="text-gray-400 mr-2">Website:</span><a href={lev.website.startsWith('http') ? lev.website : `https://${lev.website}`} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline">{lev.website}</a></div>}
            </div>
          ) : (
            <div className="text-sm text-gray-300 italic">Geen contactgegevens ingevoerd</div>
          )}
        </Card>

        {/* ── Notities ── */}
        <Card icon={Notebook} title="Notities"
          actions={editMode === 'notities' ? (
            <>
              <IconBtn icon={FloppyDisk} label="Opslaan" onClick={() => saveEdit(['notities'])} testid="save-notities" />
              <IconBtn icon={X} label="Annuleren" onClick={cancelEdit} />
            </>
          ) : (
            <IconBtn icon={PencilSimple} label="Aanpassen" onClick={() => startEdit('notities')} testid="edit-notities" />
          )}
          testid="card-notities">
          {editMode === 'notities' ? (
            <textarea className="field-input text-sm" rows={4} value={draft.notities} onChange={e => setDraft(p => ({ ...p, notities: e.target.value }))} placeholder="Interne notitie..." />
          ) : lev.notities ? (
            <p className="text-xs text-gray-700 whitespace-pre-wrap">{lev.notities}</p>
          ) : (
            <div className="text-sm text-gray-300 italic">Geen notities ingevoerd</div>
          )}
        </Card>

        {/* ── Adres ── */}
        <Card icon={MapPin} title="Adres"
          actions={editMode === 'adres' ? (
            <>
              <IconBtn icon={FloppyDisk} label="Opslaan" onClick={() => saveEdit(['straat', 'huisnr', 'postcode', 'gemeente', 'land'])} testid="save-adres" />
              <IconBtn icon={X} label="Annuleren" onClick={cancelEdit} />
            </>
          ) : (
            <>
              {(lev.straat || lev.gemeente) && (
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium border border-transparent hover:border-gray-200 text-gray-500 hover:text-[#002FA7]" data-testid="bekijk-kaart">
                  <MapPin size={12} /> Bekijk kaart
                </a>
              )}
              <IconBtn icon={PencilSimple} label="Aanpassen" onClick={() => startEdit('adres')} testid="edit-adres" />
            </>
          )}
          testid="card-adres">
          {editMode === 'adres' ? (
            <div className="space-y-2">
              <div className="grid grid-cols-4 gap-2">
                <div className="col-span-3"><label className="form-label">Straat</label><input className="field-input" value={draft.straat} onChange={e => setDraft(p => ({ ...p, straat: e.target.value }))} /></div>
                <div><label className="form-label">Nr</label><input className="field-input" value={draft.huisnr} onChange={e => setDraft(p => ({ ...p, huisnr: e.target.value }))} /></div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><label className="form-label">Postcode</label><input className="field-input" value={draft.postcode} onChange={e => setDraft(p => ({ ...p, postcode: e.target.value }))} /></div>
                <div><label className="form-label">Gemeente</label><input className="field-input" value={draft.gemeente} onChange={e => setDraft(p => ({ ...p, gemeente: e.target.value }))} /></div>
                <div><label className="form-label">Land</label><input className="field-input" value={draft.land} onChange={e => setDraft(p => ({ ...p, land: e.target.value }))} /></div>
              </div>
            </div>
          ) : (lev.straat || lev.gemeente) ? (
            <div className="text-xs text-gray-700 leading-relaxed">
              {lev.straat && <div>{lev.straat} {lev.huisnr}</div>}
              {(lev.postcode || lev.gemeente) && <div>{lev.postcode} {lev.gemeente}</div>}
              {lev.land && <div>{lev.land === 'BE' ? 'België' : lev.land}</div>}
            </div>
          ) : (
            <div className="text-sm text-gray-300 italic">Geen adres ingevoerd</div>
          )}
        </Card>

        {/* ── Contactpersonen ── */}
        <Card icon={UsersThree} title="Contactpersonen"
          actions={editMode === 'contactpersonen' ? (
            <>
              <IconBtn icon={Plus} label="Toevoegen" onClick={addContactpersoon} />
              <IconBtn icon={FloppyDisk} label="Opslaan" onClick={() => saveEdit(['contactpersonen'])} testid="save-contactpersonen" />
              <IconBtn icon={X} label="Annuleren" onClick={cancelEdit} />
            </>
          ) : (
            <IconBtn icon={Plus} label="Toevoegen" onClick={() => startEdit('contactpersonen')} testid="edit-contactpersonen" />
          )}
          testid="card-contactpersonen">
          {editMode === 'contactpersonen' ? (
            <div className="space-y-2">
              {(draft.contactpersonen || []).length === 0 && (
                <div className="text-xs text-gray-300 italic text-center py-2">Klik op "Toevoegen" om een contactpersoon toe te voegen</div>
              )}
              {(draft.contactpersonen || []).map((cp, i) => (
                <div key={i} className="p-2 border border-gray-100 bg-gray-50 space-y-1">
                  <div className="flex gap-1">
                    <input className="field-input text-xs flex-1" placeholder="Naam" value={cp.naam} onChange={e => updContactpersoon(i, 'naam', e.target.value)} />
                    <input className="field-input text-xs flex-1" placeholder="Functie" value={cp.functie} onChange={e => updContactpersoon(i, 'functie', e.target.value)} />
                    <button className="text-red-400 hover:text-red-600 px-1" onClick={() => delContactpersoon(i)}><Trash size={12} /></button>
                  </div>
                  <div className="flex gap-1">
                    <input className="field-input text-xs flex-1" placeholder="E-mail" value={cp.email} onChange={e => updContactpersoon(i, 'email', e.target.value)} />
                    <input className="field-input text-xs flex-1" placeholder="Telefoon" value={cp.telefoon} onChange={e => updContactpersoon(i, 'telefoon', e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
          ) : contactpersonenDisp.length > 0 ? (
            <div className="space-y-2">
              {contactpersonenDisp.map((cp, i) => (
                <div key={i} className="text-xs pb-2 border-b border-gray-100 last:border-0 last:pb-0">
                  <div className="font-semibold text-gray-900">{cp.naam || '(geen naam)'}{cp.functie && <span className="text-gray-400 font-normal ml-1">· {cp.functie}</span>}</div>
                  {cp.email && <div className="text-gray-500">{cp.email}</div>}
                  {cp.telefoon && <div className="text-gray-500">{cp.telefoon}</div>}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-300 italic">Geen contactpersonen</div>
          )}
        </Card>

        {/* ── Voorkeuren ── */}
        <Card icon={SlidersHorizontal} title="Voorkeuren"
          actions={editMode === 'voorkeuren' ? (
            <>
              <IconBtn icon={FloppyDisk} label="Opslaan" onClick={() => saveEdit(['actief', 'taal'])} testid="save-voorkeuren" />
              <IconBtn icon={X} label="Annuleren" onClick={cancelEdit} />
            </>
          ) : (
            <IconBtn icon={PencilSimple} label="Aanpassen" onClick={() => startEdit('voorkeuren')} testid="edit-voorkeuren" />
          )}
          testid="card-voorkeuren">
          {editMode === 'voorkeuren' ? (
            <div className="space-y-3">
              <label className="flex items-center justify-between text-xs">
                <span className="text-gray-600">Actief</span>
                <IOSToggle checked={draft.actief} onChange={v => setDraft(p => ({ ...p, actief: v }))} size="sm" testid="toggle-actief" />
              </label>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-gray-600">Taal</span>
                <select className="field-input text-xs" style={{ maxWidth: 160 }} value={draft.taal} onChange={e => setDraft(p => ({ ...p, taal: e.target.value }))}>
                  {TALEN.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
                </select>
              </div>
            </div>
          ) : (
            <div className="space-y-2 text-xs">
              <div className="flex items-center justify-between"><span className="text-gray-500">Actief</span><span className={`font-bold ${lev.actief === false ? 'text-red-500' : 'text-[#059669]'}`}>{lev.actief === false ? 'Nee' : 'Ja'}</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-500">Taal</span><span className="font-medium text-gray-700">{(TALEN.find(t => t.code === (lev.taal || '')) || TALEN[0]).label}</span></div>
            </div>
          )}
        </Card>

        {/* ── Bankrekeningen (spans 2 cols) ── */}
        <div className="lg:col-span-2">
          <Card icon={BankIcon} title="Bankrekeningen"
            actions={editMode === 'bankrekeningen' ? (
              <>
                <IconBtn icon={Plus} label="Toevoegen" onClick={addBankrek} />
                <IconBtn icon={FloppyDisk} label="Opslaan" onClick={() => saveEdit(['bankrekeningen', 'iban', 'bic'])} testid="save-bankrekeningen" />
                <IconBtn icon={X} label="Annuleren" onClick={cancelEdit} />
              </>
            ) : (
              <IconBtn icon={Plus} label="Toevoegen" onClick={() => startEdit('bankrekeningen')} testid="edit-bankrekeningen" />
            )}
            testid="card-bankrekeningen">
            {editMode === 'bankrekeningen' ? (
              <div className="space-y-2">
                {(draft.bankrekeningen || []).length === 0 && (
                  <div className="text-xs text-gray-300 italic text-center py-2">Klik op "Toevoegen" om een bankrekening toe te voegen</div>
                )}
                {(draft.bankrekeningen || []).map((br, i) => (
                  <div key={i} className="flex gap-1 items-center">
                    <input className="field-input text-xs font-mono flex-1" placeholder="IBAN (BE68...)" value={br.iban} onChange={e => updBankrek(i, 'iban', e.target.value)} />
                    <input className="field-input text-xs font-mono" style={{ maxWidth: 120 }} placeholder="BIC" value={br.bic} onChange={e => updBankrek(i, 'bic', e.target.value)} />
                    <input className="field-input text-xs flex-1" placeholder="Rekeninghouder" value={br.rekeninghouder} onChange={e => updBankrek(i, 'rekeninghouder', e.target.value)} />
                    <button className="text-red-400 hover:text-red-600 px-1" onClick={() => delBankrek(i)}><Trash size={12} /></button>
                  </div>
                ))}
              </div>
            ) : (bankrekeningenDisp.length > 0 || lev.iban) ? (
              <div className="space-y-1.5">
                {lev.iban && bankrekeningenDisp.length === 0 && (
                  <div className="flex items-center gap-3 text-xs">
                    <span className="font-mono font-semibold text-gray-900">{lev.iban}</span>
                    {lev.bic && <span className="font-mono text-gray-500">{lev.bic}</span>}
                  </div>
                )}
                {bankrekeningenDisp.map((br, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs pb-1.5 border-b border-gray-100 last:border-0">
                    <span className="font-mono font-semibold text-gray-900">{br.iban || '(geen IBAN)'}</span>
                    {br.bic && <span className="font-mono text-gray-500">{br.bic}</span>}
                    {br.rekeninghouder && <span className="text-gray-500">{br.rekeninghouder}</span>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-300 italic">Geen bankrekeningen</div>
            )}
          </Card>
        </div>
      </div>

      {/* ── Aankopen tabel ── */}
      <div className="bg-white border border-gray-200 mb-4" data-testid="aankopen-section">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-500">
            <Receipt size={14} weight="bold" /> Aankopen <span className="ml-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-bold">{filteredDocs.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative" style={{ width: 240 }}>
              <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input className="field-input text-xs" style={{ paddingLeft: 28 }} placeholder="Zoeken..." value={q} onChange={e => setQ(e.target.value)} data-testid="aankopen-search" />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead><tr>
              <th style={{ width: 32 }}>
                <input type="checkbox"
                  checked={pagedDocs.length > 0 && pagedDocs.every(d => selected.has(d.id))}
                  onChange={toggleSelectAllPage}
                  className="accent-[#002FA7] cursor-pointer"
                  data-testid="select-all-page" />
              </th>
              <th>Type</th><th>Nummer</th><th>Datum</th><th className="right">Bedrag</th><th>Status</th><th>Vervaldatum</th><th></th>
            </tr></thead>
            <tbody>
              {pagedDocs.map(d => {
                const dl = daysLeft(d.vervaldatum);
                const isSelected = selected.has(d.id);
                return (
                  <tr key={d.id} className={isSelected ? 'bg-[#F0F4FF]' : ''}>
                    <td>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(d.id)} className="accent-[#002FA7] cursor-pointer" data-testid={`select-doc-${d.id}`} />
                    </td>
                    <td className="text-xs">Factuur</td>
                    <td className="font-mono text-xs font-semibold">{d.doc_nummer || '-'}</td>
                    <td className="text-xs">{fmtDate(d.doc_datum)}</td>
                    <td className="right font-mono text-xs font-semibold">{fmtEur(d.totaal)}</td>
                    <td>
                      {d.betaald ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 bg-[#059669] text-white">Betaald</span>
                      ) : (
                        <span className="text-[10px] font-bold px-2 py-0.5 bg-[#002FA7] text-white">Wachten op betaling</span>
                      )}
                      {d.naar_accountant && <span className="ml-1 text-[10px] font-bold px-2 py-0.5 bg-purple-100 text-purple-700 border border-purple-200">Accountant</span>}
                    </td>
                    <td className="text-xs">
                      <div className="flex items-center gap-1.5">
                        <span>{fmtDate(d.vervaldatum)}</span>
                        {!d.betaald && dl !== null && (
                          dl < 0 ? <span className="text-[10px] font-bold px-1.5 py-0.5 bg-red-500 text-white">{Math.abs(dl)}d te laat</span>
                          : dl === 0 ? <span className="text-[10px] font-bold px-1.5 py-0.5 bg-orange-500 text-white">vandaag</span>
                          : <span className="text-[10px] font-bold px-1.5 py-0.5 bg-green-50 text-green-700 border border-green-200">{dl} dagen resterend</span>
                        )}
                      </div>
                    </td>
                    <td><Link to={`/inkomende-documenten/${d.id}`} className="btn btn-ghost btn-sm text-[#002FA7]" data-testid={`open-doc-${d.id}`}>Open</Link></td>
                  </tr>
                );
              })}
              {pagedDocs.length === 0 && <tr><td colSpan="8" className="text-center text-gray-400 py-8 text-sm">Geen aankopen</td></tr>}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {filteredDocs.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 text-xs text-gray-500">
            <div className="flex items-center gap-2">
              Toon
              <select value={pageSize} onChange={e => setPageSize(parseInt(e.target.value))} className="border border-gray-200 px-1.5 py-0.5 text-xs">
                <option>10</option><option>25</option><option>50</option>
              </select>
              resultaten
            </div>
            <div>
              {(curPage - 1) * pageSize + 1} tot {Math.min(curPage * pageSize, filteredDocs.length)} van {filteredDocs.length}
            </div>
            <div className="flex items-center gap-0.5">
              <button className="btn btn-ghost btn-sm" disabled={curPage === 1} onClick={() => setPage(1)}>Eerste</button>
              <button className="btn btn-ghost btn-sm" disabled={curPage === 1} onClick={() => setPage(p => p - 1)}>Vorige</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).slice(Math.max(0, curPage - 2), curPage + 1).map(p => (
                <button key={p} className={`px-2 py-0.5 text-xs font-bold ${p === curPage ? 'bg-[#002FA7] text-white' : 'hover:bg-gray-100'}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="btn btn-ghost btn-sm" disabled={curPage === totalPages} onClick={() => setPage(p => p + 1)}>Volgende</button>
              <button className="btn btn-ghost btn-sm" disabled={curPage === totalPages} onClick={() => setPage(totalPages)}>Laatste</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Bijlagen ── */}
      <div className="bg-white border border-gray-200 p-4" data-testid="bijlagen-section">
        <BijlagenPanel docId={`lev_${lev.id}`} />
      </div>
    </div>
  );
}
